import { CardWithIntent, type CardWithIntentProps } from "@clipboard-health/ui-components";
import { mergeSxProps } from "@clipboard-health/ui-components";
import { type IntentVariant } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material";
import type { Ref } from "react";
import { forwardRef } from "react";

export type ShiftBottomSheetInnerCardIntent = IntentVariant | "priority" | "tiers" | "limited";

interface ShiftBottomSheetInnerCardProps extends Omit<CardWithIntentProps, "intent"> {
  intent?: ShiftBottomSheetInnerCardIntent;
}

function borderColor(intent: ShiftBottomSheetInnerCardIntent, theme: Theme) {
  if (intent === "tiers") {
    return theme.palette.tiers?.gold.border;
  }

  if (intent === "priority") {
    return theme.palette.shift?.priority.border;
  }

  if (intent === "limited") {
    return theme.palette.tiers?.limited.light;
  }

  return undefined;
}

function intentOverride(intent: ShiftBottomSheetInnerCardIntent): IntentVariant | undefined {
  return intent === "priority" || intent === "tiers" || intent === "limited" ? undefined : intent;
}

function BaseShiftBottomSheetInnerCard(
  props: ShiftBottomSheetInnerCardProps,
  ref: Ref<HTMLDivElement>
) {
  const { sx, intent = "neutral", ...restProps } = props;

  const overriddenIntent = intentOverride(intent);

  return (
    <CardWithIntent
      {...restProps}
      ref={ref}
      intent={overriddenIntent}
      sx={mergeSxProps(
        (theme) => ({
          minHeight: "20rem",
          border: isDefined(overriddenIntent)
            ? undefined
            : `1px solid ${borderColor(intent, theme)}`,
        }),
        sx
      )}
    />
  );
}

export const ShiftBottomSheetInnerCard = forwardRef(BaseShiftBottomSheetInnerCard);
